import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Stars, Badge } from "@/lib/ui";
import { Users, HandHeart, ClipboardList, CheckCircle2, ShieldCheck } from "lucide-react";
import AdminVerifications from "@/components/AdminVerifications";
import Avatar from "@/components/Avatar";
import AdminSafetyDesk from "@/components/AdminSafetyDesk";
import AdminPayments from "@/components/AdminPayments";
import DemoSeeder from "@/components/DemoSeeder";

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border border-[#E2D9CE] rounded-2xl p-5">
      <span className={`w-11 h-11 rounded-xl ${color} flex items-center justify-center mb-3`}>
        <Icon className="w-6 h-6 text-white" />
      </span>
      <p className="text-3xl font-display font-bold text-slate-900">{value}</p>
      <p className="text-slate-600">{label}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState("helpers");

  const loadUsers = () => api.get("/admin/users").then((r) => setUsers(r.data));
  const loadAll = () => { api.get("/admin/stats").then((r) => setStats(r.data)); loadUsers(); };
  useEffect(() => { loadAll(); }, []);

  const verify = async (id, verified) => {
    await api.post(`/admin/users/${id}/verify`, { verified });
    toast.success(verified ? "Helper verified" : "Verification removed");
    loadUsers();
  };

  const helpers = users.filter((u) => u.role === "helper");
  const seekers = users.filter((u) => u.role === "seeker");
  const list = tab === "helpers" ? helpers : seekers;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
      <h1 className="text-3xl sm:text-4xl font-display font-bold text-slate-900">Admin overview</h1>
      <p className="text-lg text-slate-600 mt-1 mb-8">Verify helpers and oversee platform activity.</p>
      <DemoSeeder onDone={loadAll} />

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <Stat icon={Users} label="Total users" value={stats.total_users} color="bg-[#2E7D6B]" />
          <Stat icon={HandHeart} label="Helpers" value={stats.helpers} color="bg-[#D96B43]" />
          <Stat icon={ClipboardList} label="Open requests" value={stats.open_requests} color="bg-[#D99B26]" />
          <Stat icon={CheckCircle2} label="Completed" value={stats.completed_requests} color="bg-[#16A34A]" />
        </div>
      )}

      <div className="flex gap-2 mb-5">
        {[["helpers", `Helpers (${helpers.length})`], ["seekers", `Care Seekers (${seekers.length})`], ["verifications", "Verifications"], ["safety", "Safety desk"], ["payments", "Payments"]].map(([v, l]) => (
          <button key={v} data-testid={`admin-tab-${v}`} onClick={() => setTab(v)}
            className={`px-5 min-h-[48px] rounded-xl font-semibold transition-all ${tab === v ? "bg-[#D96B43] text-white" : "bg-white border border-[#E2D9CE] text-slate-700"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "payments" ? <AdminPayments /> : tab === "safety" ? <AdminSafetyDesk /> : tab === "verifications" ? <AdminVerifications onChanged={loadUsers} /> : (
      <div className="bg-white border border-[#E2D9CE] rounded-2xl divide-y divide-[#E2D9CE]">
        {list.length === 0 ? (
          <p className="p-6 text-slate-600">No {tab} yet.</p>
        ) : list.map((u) => (
          <div key={u.id} data-testid={`admin-user-${u.id}`} className="p-5 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Avatar user={u} size="sm" />
              <div>
                <p className="font-semibold text-slate-900">{u.name}</p>
                <p className="text-slate-500 text-sm">{u.email}</p>
                {u.role === "helper" && (
                  <div className="flex items-center gap-2 mt-1">
                    <Stars rating={u.avg_rating} size="text-sm" />
                    <span className="text-sm text-slate-500">{u.completed_jobs} jobs</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {u.is_verified ? <Badge color="teal"><ShieldCheck className="w-4 h-4" /> Verified</Badge> : u.verification_status === "pending" ? <Badge color="gold">Docs pending</Badge> : <Badge color="slate">Unverified</Badge>}
              {u.role === "helper" && (
                u.is_verified ? (
                  <button data-testid={`admin-unverify-${u.id}`} onClick={() => verify(u.id, false)}
                    className="border-2 border-[#E2D9CE] hover:border-red-400 text-slate-700 font-semibold px-4 min-h-[44px] rounded-xl transition-colors">Remove</button>
                ) : (
                  <button data-testid={`admin-verify-user-btn`} onClick={() => verify(u.id, true)}
                    className="bg-[#2E7D6B] hover:bg-[#256658] text-white font-semibold px-4 min-h-[44px] rounded-xl transition-colors">Verify</button>
                )
              )}
            </div>
          </div>
        ))}
      </div>
      )}
    </div>
  );
}
